import {
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TAB_FINANCIAL,
  RIGHT_PANEL_TAB_LABELS,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TAB_QUANTITY,
  RIGHT_PANEL_TAB_RESISTANCE,
  RIGHT_PANEL_TAB_SHORT_LABELS,
  RIGHT_PANEL_TOOL_LABELS,
  RIGHT_PANEL_CALCULATORS_DESCRIPTION,
  RIGHT_PANEL_TOOL_CALCULATORS,
} from 'constants/uiConstants';
import { RightPanelTabId } from '../../redux/slices/InsightsSlice';
import { Modal, Tooltip } from 'antd';
import React, { useRef, useState } from 'react';
import {
  PiArrowCounterClockwise,
  PiCalculator,
  PiChartPie,
  PiCurrencyDollar,
  PiLeaf,
  PiPushPinFill,
  PiPushPinSlashFill,
  PiScales,
  PiShieldCheck,
} from 'react-icons/pi';

interface RightEdgeLabelsProps {
  /** Tab IDs to display — only unpinned tabs (collapsed or floating). */
  tabIds: RightPanelTabId[];
  /** The currently floating tabs (highlighted differently). */
  floatingTabIds: RightPanelTabId[];
  /** Single click — float the tab as an overlay. */
  onTabClick: (tabId: RightPanelTabId) => void;
  /** Double click — pin the tab directly to the pinned panel. */
  onTabDoubleClick: (tabId: RightPanelTabId) => void;
  /** Pin all tabs at once. */
  onPinAll: () => void;
  /** Collapse (unpin) all tabs at once. */
  onUnpinAll: () => void;
  /** Unpin all and reset every saved floating geometry to default. */
  onResetAll: () => void;
  /**
   * When set, a Calculators label follows the tab labels and opens the
   * calculators in the pinned panel (OP-2383, OP-2585). Only passed while the
   * pinned panel is closed — otherwise its tool tabs are the way in.
   */
  onOpenCalculators?: () => void;
  /**
   * When set, every label/bulk-action is inert (no float/pin/reset) and its
   * tooltip is replaced with this message — used at org/farm tree scope
   * (OP-2333), where the Live Panel has nothing to show.
   */
  disabledMessage?: React.ReactNode;
}

const TAB_ICONS: Record<RightPanelTabId, React.ReactElement> = {
  [RIGHT_PANEL_TAB_NUTRITION]: <PiLeaf size={14} />,
  [RIGHT_PANEL_TAB_FINANCIAL]: <PiCurrencyDollar size={14} />,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: <PiChartPie size={14} />,
  [RIGHT_PANEL_TAB_QUANTITY]: <PiScales size={14} />,
  [RIGHT_PANEL_TAB_RESISTANCE]: <PiShieldCheck size={14} />,
};

/**
 * Narrow strip on the far-right edge of the page.
 * Renders a vertical label (icon + rotated text) for each non-pinned tab.
 * The icon is always upright; only the text rotates.
 *
 * Single click → float. Double click → pin.
 *
 * Uses a timer-based approach to distinguish the two: the single-click
 * action is deferred by DBLCLICK_DELAY ms so a second click within that
 * window can cancel it and fire the double-click action instead.
 */
const DBLCLICK_DELAY = 220; // ms — typical double-click window

const RightEdgeLabels: React.FC<RightEdgeLabelsProps> = ({
  tabIds,
  floatingTabIds,
  onTabClick,
  onTabDoubleClick,
  onPinAll,
  onUnpinAll,
  onResetAll,
  onOpenCalculators,
  disabledMessage,
}) => {
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const disabled = !!disabledMessage;

  // One timer shared across all labels — only one can be pending at a time.
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTabIdRef = useRef<RightPanelTabId | null>(null);

  const handleClick = (tabId: RightPanelTabId) => {
    if (clickTimerRef.current && pendingTabIdRef.current === tabId) {
      // Second click on the same tab within the delay → double-click
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      pendingTabIdRef.current = null;
      onTabDoubleClick(tabId);
    } else {
      // First click — cancel any pending timer for a different tab, then wait
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      pendingTabIdRef.current = tabId;
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        pendingTabIdRef.current = null;
        onTabClick(tabId);
      }, DBLCLICK_DELAY);
    }
  };

  if (tabIds.length === 0 && !onOpenCalculators) return null;

  return (
    <div className="absolute bottom-0 right-0 top-0 z-50 flex w-6 flex-col border-l bg-white pt-16">
      {tabIds.map((tabId) => {
        const isFloating = floatingTabIds.includes(tabId);
        return (
          <Tooltip
            key={tabId}
            title={
              disabled
                ? disabledMessage
                : tabId === RIGHT_PANEL_TAB_FINANCIAL
                  ? `${RIGHT_PANEL_TAB_LABELS[tabId]} (Plan data only)`
                  : RIGHT_PANEL_TAB_LABELS[tabId]
            }
            placement="left"
          >
            <button
              type="button"
              onClick={disabled ? undefined : () => handleClick(tabId)}
              aria-disabled={disabled}
              className={[
                'flex h-24 w-6 flex-shrink-0 select-none flex-col items-center justify-center gap-1',
                'border-b px-0.5 transition-colors last:border-b-0',
                disabled
                  ? 'cursor-not-allowed text-gray-300'
                  : isFloating
                    ? 'cursor-pointer bg-success-light text-success'
                    : 'cursor-pointer text-gray-muted hover:bg-gray-bright hover:text-gray-dark',
              ].join(' ')}
            >
              {TAB_ICONS[tabId]}
              <span className="text-[0.625rem] font-medium tracking-widest [writing-mode:vertical-lr]">
                {RIGHT_PANEL_TAB_SHORT_LABELS[tabId]}
              </span>
            </button>
          </Tooltip>
        );
      })}

      {onOpenCalculators && (
        <Tooltip
          title={
            disabled ? (
              disabledMessage
            ) : (
              <>{RIGHT_PANEL_TOOL_LABELS[RIGHT_PANEL_TOOL_CALCULATORS]}<br /><span className="opacity-75">{RIGHT_PANEL_CALCULATORS_DESCRIPTION}</span></>
            )
          }
          placement="left"
        >
          <button
            type="button"
            onClick={disabled ? undefined : onOpenCalculators}
            aria-disabled={disabled}
            className={[
              // Taller than the tab labels — "Calculators" is a long word
              // set vertically.
              'flex h-28 w-6 flex-shrink-0 select-none flex-col items-center justify-center gap-1',
              'border-b border-t px-0.5 text-gray-dark transition-colors',
              disabled
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer hover:bg-gray-bright hover:text-charcoal',
            ].join(' ')}
          >
            <PiCalculator size={14} aria-hidden />
            <span className="text-xs font-medium tracking-widest [writing-mode:vertical-lr]">
              {RIGHT_PANEL_TOOL_LABELS[RIGHT_PANEL_TOOL_CALCULATORS]}
            </span>
          </button>
        </Tooltip>
      )}

      {/* ── Bulk actions ───────────────────────────────────────────── */}
      <div className="mt-auto flex flex-col items-center gap-1 border-t py-2">
        <Tooltip
          title={
            disabled ? (
              disabledMessage
            ) : (
              <>Pin all<br /><span className="opacity-75">Dock every panel to the side bar</span></>
            )
          }
          placement="left"
        >
          <button
            type="button"
            aria-label="Pin all"
            aria-disabled={disabled}
            onClick={disabled ? undefined : onPinAll}
            className={`inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] ${disabled ? 'cursor-not-allowed text-gray-300' : 'text-secondary-dark hover:bg-gray-bright active:bg-gray-200'}`}
          >
            <PiPushPinFill size={14} />
          </button>
        </Tooltip>
        <Tooltip
          title={
            disabled ? (
              disabledMessage
            ) : (
              <>Unpin all<br /><span className="opacity-75">Collapse every panel to the edge strip</span></>
            )
          }
          placement="left"
        >
          <button
            type="button"
            aria-label="Unpin all"
            aria-disabled={disabled}
            onClick={disabled ? undefined : onUnpinAll}
            className={`inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] ${disabled ? 'cursor-not-allowed text-gray-300' : 'text-secondary-dark hover:bg-gray-bright active:bg-gray-200'}`}
          >
            <PiPushPinSlashFill size={14} />
          </button>
        </Tooltip>
        <Tooltip
          title={
            disabled ? (
              disabledMessage
            ) : (
              <>Reset all<br /><span className="opacity-75">Unpin all and clear saved positions</span></>
            )
          }
          placement="left"
        >
          <button
            type="button"
            aria-label="Reset all"
            aria-disabled={disabled}
            onClick={disabled ? undefined : () => setResetModalOpen(true)}
            className={`inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] ${disabled ? 'cursor-not-allowed text-gray-300' : 'text-secondary-dark hover:bg-gray-bright active:bg-gray-200'}`}
          >
            <PiArrowCounterClockwise size={14} />
          </button>
        </Tooltip>
      </div>

      <Modal
        open={resetModalOpen}
        title="Reset all panels?"
        okText="Reset"
        cancelText="Cancel"
        okButtonProps={{ className: 'custom-popconfirm-ok' }}
        cancelButtonProps={{ className: 'custom-popconfirm-cancel' }}
        closable
        onOk={() => {
          onResetAll();
          setResetModalOpen(false);
        }}
        onCancel={() => setResetModalOpen(false)}
      >
        <p className="text-gray-dark">
          All panels will be unpinned and their saved positions will be cleared.
          This cannot be undone from here.
        </p>
      </Modal>
    </div>
  );
};

export default RightEdgeLabels;
